import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Button,
  Grid,
  Paper,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';

const SecurityCodeTest = () => {
  const [loading, setLoading] = useState(false);
  const [testResults, setTestResults] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const runTest = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch('/api/security-code-test/run');
      if (!response.ok) throw new Error('Test failed to run');
      const data = await response.json();
      setTestResults(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    runTest(); // Auto-run on mount
  }, []);

  if (loading && !testResults) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Running security code tests...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
        <Button onClick={runTest} size="small" sx={{ ml: 2 }}>
          Retry
        </Button>
      </Alert>
    );
  }

  if (!testResults) return null;

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="flex-start"
        flexDirection={{ xs: 'column', md: 'row' }}
        gap={2}
        mb={4}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar
            sx={{
              bgcolor: 'primary.light',
              width: 56,
              height: 56,
              boxShadow: 2
            }}
          >
            <SecurityIcon sx={{ fontSize: 28 }} />
          </Avatar>
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Security Code Authentication Test
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Validates the new security code authentication flow
            </Typography>
          </Box>
        </Box>
        <Button
          startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
          onClick={runTest}
          variant="contained"
          disabled={loading}
          size="large"
          sx={{
            borderRadius: 2,
            px: 3,
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: 2
          }}
        >
          {loading ? 'Testing...' : 'Re-run Test'}
        </Button>
      </Box>

      {/* Overall Status */}
      <Card 
        elevation={3}
        sx={{ 
          mb: 4,
          borderRadius: 3,
          overflow: 'hidden',
          border: testResults.allTestsPassed ? '2px solid' : '2px solid',
          borderColor: testResults.allTestsPassed ? 'success.main' : 'error.main',
          bgcolor: testResults.allTestsPassed ? 'success.50' : 'error.50'
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" gap={3}>
            <Avatar
              sx={{
                bgcolor: testResults.allTestsPassed ? 'success.main' : 'error.main',
                width: 64,
                height: 64,
                boxShadow: 3
              }}
            >
              {testResults.allTestsPassed ? (
                <CheckCircleIcon sx={{ fontSize: 32 }} />
              ) : (
                <ErrorIcon sx={{ fontSize: 32 }} />
              )}
            </Avatar>
            <Box flex={1}>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                {testResults.allTestsPassed ? 'ALL TESTS PASSED' : 'TESTS FAILED'}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {testResults.allTestsPassed 
                  ? 'Security code authentication is ready for deployment'
                  : 'Some tests failed - review results below'}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Coverage Statistics */}
      <Card elevation={2} sx={{ mb: 4, borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 3 }}>
            Security Code Coverage
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 3, 
                  textAlign: 'center',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
              >
                <Typography variant="h3" fontWeight={700}>
                  {testResults.coverage?.total || 0}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
                  Total Customers
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 3, 
                  textAlign: 'center',
                  background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                  color: 'white',
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
              >
                <Typography variant="h3" fontWeight={700}>
                  {testResults.coverage?.withCode || 0}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
                  With Security Code
                </Typography>
                <Chip 
                  label={`${testResults.coverage?.percentage || 0}%`}
                  size="small"
                  sx={{ 
                    mt: 1,
                    bgcolor: 'rgba(255,255,255,0.3)',
                    color: 'white',
                    fontWeight: 600
                  }}
                />
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 3, 
                  textAlign: 'center',
                  background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                  color: 'white',
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
              >
                <Typography variant="h3" fontWeight={700}>
                  {testResults.coverage?.withoutCode || 0}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
                  Without Code
                </Typography>
                <Chip 
                  label={`${testResults.coverage?.withoutPercentage || 0}%`}
                  size="small"
                  sx={{ 
                    mt: 1,
                    bgcolor: 'rgba(255,255,255,0.3)',
                    color: 'white',
                    fontWeight: 600
                  }}
                />
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 3, 
                  textAlign: 'center',
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  color: 'white',
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
              >
                <Typography variant="h3" fontWeight={700}>
                  ${testResults.balanceStats?.avgBalance || '0.00'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
                  Avg Balance
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Test Scenarios */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            🧪 Authentication Scenarios
          </Typography>
          
          {testResults.scenarios?.map((scenario: any, index: number) => (
            <Box key={index} sx={{ mb: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {scenario.title}
                </Typography>
                {scenario.passed ? (
                  <CheckCircleIcon color="success" />
                ) : (
                  <ErrorIcon color="error" />
                )}
              </Box>
              
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                {scenario.customer && (
                  <Box mb={2}>
                    <Typography variant="body2" fontWeight="bold">Sample Customer:</Typography>
                    <Typography variant="body2">
                      Name: {scenario.customer.name} (#{scenario.customer.custnum})
                    </Typography>
                    <Typography variant="body2">
                      Phone: {scenario.customer.phone || 'N/A'}
                    </Typography>
                    {scenario.customer.securityCode && (
                      <Typography variant="body2">
                        Security Code: ****{scenario.customer.securityCode.slice(-2)} 
                        ({scenario.customer.securityCode.length} digits)
                      </Typography>
                    )}
                  </Box>
                )}
                
                <Divider sx={{ my: 1 }} />
                
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  Authentication Flow:
                </Typography>
                {scenario.steps?.map((step: any, idx: number) => (
                  <Typography key={idx} variant="body2" sx={{ ml: 2, mb: 0.5 }}>
                    {step}
                  </Typography>
                ))}
                
                {scenario.securityWarning && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    🚨 {scenario.securityWarning}
                  </Alert>
                )}
              </Paper>
            </Box>
          ))}
        </CardContent>
      </Card>

      {/* Sample Customers */}
      {testResults.sampleCustomers && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              👥 Sample Customers
            </Typography>
            
            <Box mb={3}>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon color="success" fontSize="small" />
                Customers WITH Security Codes
              </Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Account #</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Code (Masked)</TableCell>
                      <TableCell>Balance</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {testResults.sampleCustomers.withCode?.map((cust: any, idx: number) => (
                      <TableRow key={idx}>
                        <TableCell>{cust.name}</TableCell>
                        <TableCell>{cust.custnum}</TableCell>
                        <TableCell>{cust.phone || 'N/A'}</TableCell>
                        <TableCell>****{cust.codeDisplay}</TableCell>
                        <TableCell>${cust.balance}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningIcon color="warning" fontSize="small" />
                Customers WITHOUT Security Codes (Use Last 4 Digits)
              </Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Account #</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Auth Method</TableCell>
                      <TableCell>Balance</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {testResults.sampleCustomers.withoutCode?.map((cust: any, idx: number) => (
                      <TableRow key={idx}>
                        <TableCell>{cust.name}</TableCell>
                        <TableCell>{cust.custnum}</TableCell>
                        <TableCell>{cust.phone || 'N/A'}</TableCell>
                        <TableCell>
                          <Chip label="Last 4 Digits" size="small" color="warning" />
                        </TableCell>
                        <TableCell>${cust.balance}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Security Validation */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon color="error" />
            Security Violation Prevention
          </Typography>
          
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="bold">✅ CORRECT Behavior:</Typography>
            <Typography variant="body2">
              AI: "For security, can you provide your security code?"<br/>
              Customer: "39642"<br/>
              AI: [Silently compares] ✅ → "Thank you! May I have your name?"
            </Typography>
          </Alert>

          <Alert severity="error">
            <Typography variant="body2" fontWeight="bold">❌ FORBIDDEN Behavior (Should NEVER happen):</Typography>
            <Typography variant="body2">
              ❌ AI: "Your security code is 39642"<br/>
              ❌ AI: "I can see your security code is 39642"<br/>
              ❌ AI: "Let me verify: 3-9-6-4-2"
            </Typography>
            <Typography variant="body2" fontWeight="bold" sx={{ mt: 1 }}>
              🚨 The AI is explicitly instructed to NEVER reveal the code!
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      {/* Deployment Status */}
      {testResults.allTestsPassed && (
        <Alert severity="success" sx={{ mt: 3 }}>
          <Typography variant="h6">🚀 Ready for Deployment!</Typography>
          <Typography variant="body2">
            All tests passed. You can now deploy the updated prompts to Retell AI.
          </Typography>
          <Box mt={2}>
            <Typography variant="body2" fontWeight="bold">Next Steps:</Typography>
            <Typography variant="body2">
              1. Upload FONA-VOICE-PROMPT to Retell AI<br/>
              2. Upload FONA-CHAT-PROMPT to Retell AI<br/>
              3. Test with real customers (both with and without codes)<br/>
              4. Monitor authentication success rates
            </Typography>
          </Box>
        </Alert>
      )}
    </Box>
  );
};

export default SecurityCodeTest;

